(() => {
  const SELECTOR = '[data-faj-campaign]';
  const instances = new WeakMap();

  const parseBoolean = (value, fallback = false) => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  };

  const waitForFlickity = (timeout = 5000) => new Promise((resolve) => {
    if (typeof window.Flickity === 'function') {
      resolve(window.Flickity);
      return;
    }

    const started = performance.now();
    const timer = window.setInterval(() => {
      if (typeof window.Flickity === 'function') {
        window.clearInterval(timer);
        resolve(window.Flickity);
      } else if (performance.now() - started >= timeout) {
        window.clearInterval(timer);
        resolve(null);
      }
    }, 60);
  });

  class FajCampaignSlider {
    constructor(root) {
      this.root = root;
      this.carousel = root.querySelector('[data-faj-campaign-carousel]');
      this.slides = Array.from(root.querySelectorAll('[data-faj-campaign-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-faj-campaign-dot]'));
      this.prev = root.querySelector('[data-faj-campaign-prev]');
      this.next = root.querySelector('[data-faj-campaign-next]');
      this.flickity = null;
      this.observer = null;
      this.resizeObserver = null;
      this.reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
      this.transitionDuration = Math.max(Number.parseInt(root.dataset.transitionDuration, 10) || 800, 300);
      this.flickityTimeout = Math.max(Number.parseInt(root.dataset.flickityTimeout, 10) || 5000, 1000);
      this.pauseOffscreen = parseBoolean(root.dataset.pauseOffscreen, true);
      this.boundVisibility = this.handleVisibility.bind(this);
      this.boundResize = this.handleResize.bind(this);

      if (!this.carousel || this.slides.length < 2) {
        this.updateSelected(0);
        return;
      }
      this.init();
    }

    getPhysics(duration) {
      const clamped = Math.min(Math.max(duration, 400), 1400);
      const fastness = 1 - ((clamped - 400) / 1000);
      return {
        selectedAttraction: Number((0.035 + (fastness * 0.075)).toFixed(3)),
        friction: Number((0.3 + (fastness * 0.34)).toFixed(3))
      };
    }

    async init() {
      const Flickity = await waitForFlickity(this.flickityTimeout);
      if (!Flickity || !document.documentElement.contains(this.root)) {
        this.root.classList.add('is-flickity-unavailable');
        this.updateSelected(0);
        return;
      }

      this.carousel.classList.add('is-flickity-initializing');
      const autoplay = parseBoolean(this.root.dataset.autoplay, true) && !this.reduceMotion
        ? Number(this.root.dataset.autoplaySpeed || 5000)
        : false;
      const loop = parseBoolean(this.root.dataset.wrapAround, true);
      this.loop = loop;
      const physics = this.getPhysics(this.transitionDuration);

      this.flickity = new Flickity(this.carousel, {
        cellSelector: '[data-faj-campaign-slide]',
        cellAlign: 'left',
        contain: !loop,
        wrapAround: loop,
        draggable: parseBoolean(this.root.dataset.draggable, true),
        dragThreshold: 8,
        prevNextButtons: false,
        pageDots: false,
        autoPlay: autoplay,
        pauseAutoPlayOnHover: parseBoolean(this.root.dataset.pauseHover, true),
        adaptiveHeight: true,
        accessibility: true,
        setGallerySize: true,
        resize: true,
        rightToLeft: document.documentElement.dir === 'rtl',
        selectedAttraction: physics.selectedAttraction,
        friction: physics.friction
      });

      this.carousel.classList.remove('is-flickity-initializing');
      this.root.classList.add('is-flickity-ready');
      this.bindEvents();
      this.updateSelected(this.flickity.selectedIndex || 0);
      this.resizeAfterAssets();
      if (this.pauseOffscreen) this.observeVisibility();
      document.addEventListener('visibilitychange', this.boundVisibility);
      window.addEventListener('resize', this.boundResize, { passive: true });
    }

    bindEvents() {
      this.flickity.on('select', () => this.updateSelected(this.flickity.selectedIndex));
      this.flickity.on('settle', () => this.flickity.resize());

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.fajCampaignDot || 0);
          this.flickity.select(index);
        });
      });

      this.prev?.addEventListener('click', () => this.flickity.previous(this.loop));
      this.next?.addEventListener('click', () => this.flickity.next(this.loop));

      this.slides.forEach((slide, index) => {
        slide.addEventListener('focusin', () => {
          if (this.flickity.selectedIndex !== index) this.flickity.select(index);
        });
      });
    }

    updateSelected(index) {
      this.slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === index;
        slide.classList.toggle('is-selected', selected);
        slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
        if ('inert' in slide) slide.inert = !selected;
      });

      this.dots.forEach((dot, dotIndex) => {
        const selected = dotIndex === index;
        dot.classList.toggle('is-selected', selected);
        dot.setAttribute('aria-selected', selected ? 'true' : 'false');
        dot.tabIndex = selected ? 0 : -1;
      });
    }

    resizeAfterAssets() {
      const images = Array.from(this.root.querySelectorAll('img'));
      images.forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', () => this.flickity?.resize(), { once: true });
      });

      if (document.fonts?.ready) document.fonts.ready.then(() => this.flickity?.resize());
      window.requestAnimationFrame(() => this.flickity?.resize());

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.flickity?.resize());
        this.resizeObserver.observe(this.root);
      }
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!this.flickity?.player) return;
          if (entry.isIntersecting) this.flickity.unpausePlayer();
          else this.flickity.pausePlayer();
        });
      }, { threshold: 0.15 });
      this.observer.observe(this.root);
    }

    handleVisibility() {
      if (!this.flickity?.player) return;
      if (document.hidden) this.flickity.pausePlayer();
      else if (!this.pauseOffscreen || !this.observer) this.flickity.unpausePlayer();
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.flickity?.resize(), 100);
    }

    selectBlock(blockId) {
      const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (index >= 0 && this.flickity) {
        this.flickity.select(index, false, true);
        this.flickity.pausePlayer();
      }
    }

    destroy() {
      window.clearTimeout(this.resizeTimer);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.boundVisibility);
      window.removeEventListener('resize', this.boundResize);
      this.flickity?.destroy();
      this.flickity = null;
    }
  }

  const initRoot = (root) => {
    if (!root || instances.has(root)) return;
    const instance = new FajCampaignSlider(root);
    instances.set(root, instance);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.(SELECTOR).forEach(initRoot);
    if (scope.matches?.(SELECTOR)) initRoot(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);
    const instance = root ? instances.get(root) : null;
    instance?.destroy();
    if (root) instances.delete(root);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(SELECTOR);
    const instance = root ? instances.get(root) : null;
    instance?.selectBlock(event.detail.blockId);
  });
})();
